@let bookingStep = bookingFlow.state().step;
@let bookingSessionActive = bookingFlow.inProgress();
@let bookingFlowVisible = bookingSessionActive && bookingStep !== 'loading';
@let openingHourDays = venue.openingHours | openingHours;

<ng-template #storefrontFooter>
    <footer class="storefront-footer hstack flex-wrap justify-content-center gap-3 bg-white text-center small px-3 py-3">
        <p class="mb-0">
            {{ venue.displayName }} collaborates with
            <a
                style="color: var(--ts-primary-content-base)"
                href="https://www.tablesense.com/?utm_source=venue_storefront&amp;utm_medium=booking_channel&amp;utm_campaign=customer_booking"
                target="_blank"
                rel="noopener noreferrer"
            >TableSense</a>
            to simplify operations and create lasting guest experiences.
        </p>
        @if (bookingPolicyPath; as policyPath) {
            <a
                [href]="policyPath"
                style="color: var(--ts-primary-content-base)"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venue-storefront-booking-policy"
            >Terms and Conditions</a>
        }
        @if (venue.paymentsAccount) {
            <div
                class="hstack flex-wrap justify-content-center gap-2"
                role="group"
                aria-label="Accepted payment methods"
            >
                <img ngSrc="assets/payment-cards/mastercard.svg" width="38" height="24" alt="Mastercard" />
                <img ngSrc="assets/payment-cards/visa.svg" width="38" height="24" alt="Visa" />
                <img ngSrc="assets/payment-cards/american-express.svg" width="38" height="24" alt="American Express" />
                <img ngSrc="assets/payment-cards/apple-pay.svg" width="38" height="24" alt="Apple Pay" />
                <img ngSrc="assets/payment-cards/google-pay.svg" width="38" height="24" alt="Google Pay" />
            </div>
        }
    </footer>
</ng-template>

<ng-template #bookingSession>
    <div
        class="storefront-booking-scroll position-absolute top-0 end-0 bottom-0 start-0 z-2 overflow-x-hidden overflow-y-auto"
        data-testid="storefront-booking-scroll"
    >
        <div
            class="container pb-4 py-lg-4 storefront-focused-flow bg-white"
            data-testid="storefront-focused-flow"
        >
            <app-venue-storefront-booking-flow [venue]="venue" />
        </div>

        <ng-container [ngTemplateOutlet]="storefrontFooter" />
    </div>
</ng-template>

<main
    class="storefront"
    [class.overflow-hidden]="bookingFlowVisible"
    [attr.data-booking-step]="bookingStep"
    [attr.data-booking-session]="bookingSessionActive"
    [style.height]="bookingFlowVisible ? '100dvh' : null"
    [style.--storefront-header-height]="'61px'"
    data-testid="venue-storefront"
>
    <header class="storefront-header">
        <div class="container text-center">
            <h1 class="m-0 lh-1 fs-5">
                <button
                    class="btn btn-lg btn-link fw-bold mb-0 p-0 text-reset text-decoration-none storefront-header-name"
                    style="min-height: 28px"
                    type="button"
                    data-testid="venue-storefront-header-name"
                    [attr.aria-label]="'Return to ' + venue.displayName + ' storefront'"
                    (click)="bookingFlow.returnToVenue()"
                >
                    {{ venue.displayName }}
                </button>
            </h1>
        </div>
    </header>

    <div
        class="storefront-stage"
        [class.position-relative]="bookingFlowVisible"
        [class.d-block]="bookingFlowVisible"
        [class.overflow-hidden]="bookingFlowVisible"
        [style.height]="bookingFlowVisible ? 'calc(100dvh - var(--storefront-header-height))' : null"
        [style.min-height]="bookingFlowVisible ? '0' : null"
    >
        <div
            class="storefront-shell"
            [attr.inert]="bookingSessionActive ? '' : null"
        >
            <div class="container py-4 py-lg-4 storefront-content">
            <div class="row g-4 storefront-layout">
                <div
                    class="col-12 col-md-8 storefront-main-column"
                    data-testid="storefront-main"
                >

                    <div
                        class="d-grid align-items-center gap-3 mb-3 storefront-summary"
                        style="grid-template-columns: minmax(0, 1fr) auto"
                    >
                        <div
                            class="d-flex align-items-center gap-2 text-secondary"
                            style="min-width: 0"
                        >
                            <img
                                ngSrc="assets/location.svg"
                                width="16"
                                height="16"
                                alt=""
                                aria-hidden="true"
                            />
                            <span
                                data-testid="venue-storefront-city-region"
                                class="small text-muted text-break flex-grow-1"
                                style="min-width: 0"
                            >
                                {{ cityAndRegion }}
                            </span>
                        </div>
                        <button
                            class="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center align-self-center gap-2 flex-shrink-0 text-nowrap fw-normal"
                            type="button"
                            data-testid="venue-storefront-share"
                            (click)="shareVenue()"
                        >
                            <span>Share</span>
                            <span
                                class="d-inline-block bg-primary storefront-share-icon"
                                style="width: 1rem; height: 1rem; mask: url('assets/share/normal.svg') center / contain no-repeat; -webkit-mask: url('assets/share/normal.svg') center / contain no-repeat;"
                                aria-hidden="true"
                            ></span>
                        </button>
                    </div>

                    @if (venue.description) {
                        <section
                            class="storefront-description mb-4"
                            aria-label="About this venue"
                            data-testid="venue-storefront-description"
                        >
                            <div
                                id="venue-storefront-description-content"
                                class="quill-flush"
                            >
                                <quill-view
                                    format="html"
                                    theme="bubble"
                                    [content]="descriptionContent"
                                    [sanitize]="true"
                                >
                                </quill-view>
                            </div>
                            @if (descriptionCanTruncate) {
                                <button
                                    class="btn btn-link p-0 mt-2 lh-base text-primary text-decoration-none"
                                    type="button"
                                    data-testid="venue-description-read-more"
                                    aria-controls="venue-storefront-description-content"
                                    [attr.aria-expanded]="descriptionExpanded"
                                    (click)="toggleDescription()"
                                >
                                    {{ descriptionExpanded ? 'Read less' : 'Read more' }}
                                </button>
                            }
                        </section>
                    }

                    @if (promotionalImageIds.length > 0) {
                        <section
                            class="row g-3 mb-4 storefront-images"
                            aria-label="Venue images"
                            data-testid="venue-storefront-images"
                        >
                            @for (imageId of promotionalImageIds; track imageId; let imageIndex = $index) {
                                <div
                                    [class.col-12]="promotionalImageIds.length === 1"
                                    [class.col-6]="promotionalImageIds.length > 1"
                                >
                                    <div
                                        class="storefront-promotional-image-frame overflow-hidden rounded-5"
                                        style="aspect-ratio: 16 / 9; max-height: 256px"
                                    >
                                        <img
                                            class="storefront-promotional-image d-block w-100 h-100"
                                            [ngSrc]="promotionalImageUrl(imageId)"
                                            width="1200"
                                            height="675"
                                            [alt]="venue.displayName + ' image ' + (imageIndex + 1)"
                                        />
                                    </div>
                                </div>
                            }
                        </section>
                    }

                    @if (!bookingSessionActive) {
                        <app-venue-storefront-booking-flow
                            [venue]="venue"
                        />
                    }

                    @if (upcomingEvents.length > 0) {
                        <section
                            class="storefront-events mt-4"
                            aria-labelledby="upcoming-events-heading"
                        >
                            <h2
                                id="upcoming-events-heading"
                                class="h4 fw-bold mb-3"
                            >
                                Upcoming events
                            </h2>

                            <div class="row g-4">
                                @for (upcomingEvent of upcomingEvents; track upcomingEvent.event.id) {
                                    <div class="col-12 col-md-6">
                                        <app-venue-storefront-event-card
                                            [event]="upcomingEvent.event"
                                            [nextDateTime]="upcomingEvent.nextDateTime"
                                            [currencyCode]="venue.currencyCode"
                                            (learnMore)="openEventModal(upcomingEvent)"
                                        />
                                    </div>
                                }
                            </div>
                        </section>
                    }
                </div>

                <div
                    class="col-12 col-md-4 storefront-sidebar-column"
                    data-testid="storefront-sidebar-column"
                >
                    <div class="vstack gap-3 storefront-sidebar">
                        @if (openingHourDays.length > 0) {
                            <section
                                class="storefront-card"
                                aria-labelledby="venue-opening-hours-heading"
                                data-testid="venue-storefront-opening-hours"
                            >
                                <div class="card-body hstack gap-3 align-items-start p-0">
                                    <i
                                        class="bi bi-clock fs-6 text-dark storefront-card__icon"
                                        aria-hidden="true"
                                    ></i>
                                    <div class="vstack gap-2 storefront-card__content">
                                        <h2
                                            id="venue-opening-hours-heading"
                                            class="text-secondary small fw-normal mb-0"
                                        >
                                            Opening hours
                                        </h2>
                                        @if (openingHoursExpanded) {
                                            <dl
                                                id="venue-opening-hours-days"
                                                class="vstack gap-2 mb-0"
                                            >
                                                @for (day of openingHourDays; track day.dayOfWeek) {
                                                    <div
                                                        class="opening-hours__day d-flex justify-content-between align-items-start gap-3"
                                                        [class.opening-hours__day--today]="day.dayOfWeek === todayDayOfWeek"
                                                        [attr.data-opening-hours-day]="day.dayOfWeek"
                                                    >
                                                        <dt class="opening-hours__day-name">{{ day.day }}</dt>
                                                        <dd class="opening-hours__times mb-0">
                                                            @for (time of day.times; track time.start + time.end) {
                                                                <span
                                                                    class="opening-hours__start"
                                                                    data-opening-hours-start
                                                                >{{ time.start }}</span>
                                                                <span aria-hidden="true">–</span>
                                                                <span data-opening-hours-end>{{ time.end }}</span>
                                                            } @empty {
                                                                <span class="opening-hours__closed">Closed</span>
                                                            }
                                                        </dd>
                                                    </div>
                                                }
                                            </dl>
                                        } @else {
                                            @for (day of openingHourDays; track day.dayOfWeek) {
                                                @if (day.dayOfWeek === todayDayOfWeek) {
                                                    <p
                                                        id="venue-opening-hours-days"
                                                        class="mb-0"
                                                        data-opening-hours-today
                                                    >
                                                        Today:
                                                        @for (time of day.times; track time.start + time.end) {
                                                            @if (!$first) {
                                                                <span aria-hidden="true">, </span>
                                                            }
                                                            <span>{{ time.start }} - {{ time.end }}</span>
                                                        } @empty {
                                                            Closed
                                                        }
                                                    </p>
                                                }
                                            }
                                        }
                                        <button
                                            class="btn btn-link p-0 text-decoration-none fw-normal align-self-start text-start"
                                            type="button"
                                            aria-controls="venue-opening-hours-days"
                                            [attr.aria-expanded]="openingHoursExpanded"
                                            data-testid="venue-opening-hours-toggle"
                                            (click)="toggleOpeningHours()"
                                        >
                                            {{ openingHoursExpanded ? 'Show today only' : 'See all hours' }}
                                        </button>
                                    </div>
                                </div>
                            </section>
                        }

                        @if (mapUrl) {
                            <div class="vstack gap-2" data-testid="venue-storefront-map">
                                <iframe
                                    class="w-100 storefront-card p-0"
                                    height="256"
                                    style="border:0"
                                    loading="lazy"
                                    allowfullscreen
                                    referrerpolicy="no-referrer-when-downgrade"
                                    [src]="mapUrl"
                                    [title]="'Map showing ' + venue.displayName"
                                >
                                </iframe>
                                @if (directionsUrl) {
                                    <a
                                        [href]="directionsUrl"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="btn btn-outline-secondary btn-sm w-100 hstack gap-2 justify-content-center"
                                    >
                                        <span>Open in Google Maps</span>
                                        <img
                                            src="assets/share/normal.svg"
                                            width="16"
                                            height="16"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                    </a>
                                }
                            </div>
                        }

                        <div class="card-stack">
                            @if (venue.phoneNumber) {
                                <div class="storefront-card">
                                    <div class="card-body hstack gap-3 align-items-start">
                                        <i
                                            class="bi bi-telephone fs-6 text-dark storefront-card__icon"
                                            aria-hidden="true"
                                        ></i>
                                        <div class="vstack gap-1 align-items-start storefront-card__content">
                                            <span class="text-secondary small">Phone</span>
                                            <a
                                                [href]="'tel:' + venue.phoneNumber"
                                                class="text-primary text-decoration-none"
                                            >{{ venue.phoneNumber | phoneNumber }}</a>
                                        </div>
                                    </div>
                                </div>
                            }

                            <div class="storefront-card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i
                                        class="bi bi-geo-alt fs-6 text-dark storefront-card__icon"
                                        aria-hidden="true"
                                    ></i>
                                    <div class="vstack gap-2 align-items-start storefront-card__content">
                                        <span class="text-secondary small">Address</span>
                                        <span
                                            class="text-dark"
                                            data-testid="venue-storefront-address"
                                        >{{ formattedAddress }}</span>
                                        <button
                                            class="btn btn-link p-0 text-decoration-none fw-normal text-start"
                                            type="button"
                                            data-testid="venue-storefront-copy-address"
                                            (click)="copyAddress()"
                                        >
                                            Copy address
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        @if (voucherStorefrontPath) {
                            <section
                                class="storefront-card storefront-vouchers order-first order-md-0"
                                aria-labelledby="venue-vouchers-heading"
                                data-testid="venue-storefront-vouchers"
                            >
                                <div class="card-body hstack gap-3 align-items-start p-0">
                                    <img
                                        ngSrc="assets/gift-voucher/normal.svg"
                                        class="storefront-card__icon"
                                        width="16"
                                        height="16"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <div class="vstack gap-2 align-items-start storefront-card__content">
                                        <h2
                                            id="venue-vouchers-heading"
                                            class="text-secondary small fw-normal mb-0"
                                        >
                                            Gift vouchers
                                        </h2>
                                        <p class="mb-0">
                                            Give someone a gift from {{ venue.voucherDisplayName() }}.
                                        </p>
                                        <a
                                            [href]="voucherStorefrontPath"
                                            class="btn btn-link p-0 text-decoration-none hstack gap-2"
                                        >
                                            <span>View gift vouchers</span>
                                        </a>
                                    </div>
                                </div>
                            </section>
                        }

                    </div>
                </div>
            </div>
            </div>

        </div>

        @if (bookingStep === 'loading') {
            <div
                class="storefront-loading-scrim"
                data-testid="storefront-loading-scrim"
                aria-hidden="true"
            ></div>
        }

        @if (bookingSessionActive) {
            @switch (bookingStep) {
                @case ('search') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('loading') {
                    <app-venue-storefront-booking-flow [venue]="venue" />
                    <ng-container [ngTemplateOutlet]="storefrontFooter" />
                }
                @case ('reason') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('slots') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('waitlist') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('upsell') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('policy') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('details') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('waiting') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('order-payment') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
                @case ('confirmation') {
                    <ng-container [ngTemplateOutlet]="bookingSession" />
                }
            }
        } @else {
            <ng-container [ngTemplateOutlet]="storefrontFooter" />
        }
    </div>
</main>
